import { z } from 'zod';
import type { ReactNode } from 'react';
import { connectorCommonProps, type ConnectorKonvaArgs, type ConnectorManifest } from '../../../lib/diagram/connector-manifest.types';
import { registerConnector } from '../../../lib/diagram/registries';
import { finalizeConnectorExportWithLabel } from '../../../lib/diagram/connector-label-chip';
import { resolveGenogramExportFontStack } from '../utils/exportFontStack';
import i18n from '../../../i18n/config';

function escapeText(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

const EXPORT_MARRIAGE_LABEL_FONT_SIZE = 12;
const EXPORT_MARRIAGE_LABEL_FILL = '#6b7280';
const EXPORT_LABEL_BACKGROUND = '#FFFFFF';
const EXPORT_LABEL_VISUAL_NUDGE_Y = 12;

function resolveBezierExportLabelYOffset(props?: Record<string, unknown>): number {
  if (!props) return 12;
  if (props.controlling === true || props.jealous === true) {
    return 22;
  }
  if (
    props.doubleLine === true ||
    props.tripleLine === true ||
    props.emotionalConnection === true ||
    props.fusedHostile === true ||
    props.distanceHostile === true ||
    props.closeHostile === true ||
    props.distantViolence === true ||
    props.closeViolence === true ||
    props.midTicks === true ||
    props.midCircle === true ||
    props.cutoffRepaired === true ||
    props.neverMet === true ||
    props.inLoveHarmony === true ||
    props.distrust === true ||
    props.wave === true ||
    props.focusedNegatively === true ||
    props.abusePattern === true ||
    props.physicalAbuse === true ||
    props.emotionalAbuse === true ||
    props.sexualAbuse === true ||
    props.neglectAbuse === true ||
    props.manipulative === true ||
    props.fanAdmirer === true ||
    props.limerence === true
  ) {
    return 18;
  }
  return 12;
}

const marriageProps = connectorCommonProps.extend({
  relation: z.literal('marriage').default('marriage'),
  label: z.string().optional(),
  divorced: z.boolean().optional(),
  divorcedV2: z.boolean().optional(),
  separated: z.boolean().optional(),
  nullity: z.boolean().optional(),
  engaged: z.boolean().optional(),
  house: z.boolean().optional(),
});
type MarriageProps = z.infer<typeof marriageProps>;

type MarriageKonvaRenderer = (args: ConnectorKonvaArgs<Record<string, unknown>>) => ReactNode;
type ChildKonvaRenderer = (args: ConnectorKonvaArgs<Record<string, unknown>>) => ReactNode;
type EmotionalKonvaRenderer = (args: ConnectorKonvaArgs<Record<string, unknown>>) => ReactNode;

let _marriageKonvaRenderer: MarriageKonvaRenderer | null = null;
let _childKonvaRenderer: ChildKonvaRenderer | null = null;
let _emotionalKonvaRenderer: EmotionalKonvaRenderer | null = null;
const marriageKonvaNative = (args: ConnectorKonvaArgs<Record<string, unknown>>) =>
  _marriageKonvaRenderer?.(args) ?? null;
void _childKonvaRenderer; void _emotionalKonvaRenderer;

export function setMarriageKonvaRenderer(renderer: MarriageKonvaRenderer | null) {
  _marriageKonvaRenderer = renderer;
}

export function setChildKonvaRenderer(renderer: ChildKonvaRenderer | null) {
  _childKonvaRenderer = renderer;
}

export function setEmotionalKonvaRenderer(renderer: EmotionalKonvaRenderer | null) {
  _emotionalKonvaRenderer = renderer;
}

const lineProps = connectorCommonProps.extend({
  label: z.string().optional(),
  routingType: z.enum(['straight', 'orthogonal', 'orthogonalRounded', 'bezier']).optional(),
  tension: z.number().optional(),
  fromAnchor: z.enum(['top', 'right', 'bottom', 'left']).optional(),
  toAnchor: z.enum(['top', 'right', 'bottom', 'left']).optional(),
  fromOffset: z.number().min(0).max(1).optional(),
  toOffset: z.number().min(0).max(1).optional(),
});
type LineProps = z.infer<typeof lineProps>;

const childProps = connectorCommonProps.extend({
  relation: z.literal('child').default('child'),
});
type ChildProps = z.infer<typeof childProps>;
type ChildSvgProps = ChildProps & { __exportChildExtension?: { x1: number; x2: number; y: number } };

function svgArrowMarker(id: string, color: string, size: number, type: string = 'triangle') {
  const w = size;
  const h = size;

  switch (type) {
    case 'circle':
      return `<marker id="${id}" markerWidth="${w}" markerHeight="${h}" refX="${w/2}" refY="${h/2}" orient="auto" markerUnits="strokeWidth"><circle cx="${w/2}" cy="${h/2}" r="${w/2}" fill="${color}"/></marker>`;
    
    case 'diamond':
      return `<marker id="${id}" markerWidth="${w}" markerHeight="${h}" refX="${w}" refY="${h/2}" orient="auto" markerUnits="strokeWidth"><path d="M${w},${h/2} L${w/2},0 L0,${h/2} L${w/2},${h} Z" fill="${color}"/></marker>`;
    
    case 'bar':
      // Make bar arrowheads thinner (thinner stroke width)
      return `<marker id="${id}" markerWidth="${w}" markerHeight="${h}" refX="${w}" refY="${h/2}" orient="auto" markerUnits="strokeWidth"><line x1="${w/2}" y1="0" x2="${w/2}" y2="${h}" stroke="${color}" stroke-width="1"/></marker>`;

    case 'chevron':
      return `<marker id="${id}" markerWidth="14" markerHeight="16" refX="14" refY="8" orient="auto" markerUnits="userSpaceOnUse"><path d="M0,0 L12,8 L0,16" fill="none" stroke="${color}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/></marker>`;
    
    case 'triangle':
    default:
      return `<marker id="${id}" markerWidth="${w}" markerHeight="${h}" refX="${w}" refY="${h/2}" orient="auto" markerUnits="strokeWidth"><path d="M0,0 L${w},${h/2} L0,${h} Z" fill="${color}"/></marker>`;
  }
}

function polylineD(points: number[], routingType?: string) {
  if (!points || points.length < 4) return '';

  // For bezier curves, we need at least 4 points (start, c1, c2, end)
  if (routingType === 'bezier' && points.length >= 8) {
    let d = `M ${points[0]} ${points[1]}`;
    // Use cubic bezier curve: C c1x,c1y c2x,c2y endx,endy
    d += ` C ${points[2]} ${points[3]} ${points[4]} ${points[5]} ${points[6]} ${points[7]}`;
    return d;
  }

  // For straight lines or when we don't have enough points for bezier
  let d = `M ${points[0]} ${points[1]}`;
  for (let i = 2; i < points.length; i += 2) d += ` L ${points[i]} ${points[i+1]}`;
  return d;
}

type PathPoint = { x: number; y: number };

function pathPointAt(points: number[], t: number): PathPoint {
  const clamped = Math.min(1, Math.max(0, t));
  if (points.length >= 8) {
    const p0 = { x: points[0], y: points[1] };
    const p1 = { x: points[2], y: points[3] };
    const p2 = { x: points[4], y: points[5] };
    const p3 = { x: points[6], y: points[7] };
    const mt = 1 - clamped;
    return {
      x: mt * mt * mt * p0.x + 3 * mt * mt * clamped * p1.x + 3 * mt * clamped * clamped * p2.x + clamped * clamped * clamped * p3.x,
      y: mt * mt * mt * p0.y + 3 * mt * mt * clamped * p1.y + 3 * mt * clamped * clamped * p2.y + clamped * clamped * clamped * p3.y,
    };
  }
  const last = points.length - 2;
  return {
    x: points[0] + (points[last] - points[0]) * clamped,
    y: points[1] + (points[last + 1] - points[1]) * clamped,
  };
}

function pathTangentAt(points: number[], t: number): PathPoint {
  const clamped = Math.min(1, Math.max(0, t));
  if (points.length >= 8) {
    const p0 = { x: points[0], y: points[1] };
    const p1 = { x: points[2], y: points[3] };
    const p2 = { x: points[4], y: points[5] };
    const p3 = { x: points[6], y: points[7] };
    const mt = 1 - clamped;
    return {
      x: 3 * mt * mt * (p1.x - p0.x) + 6 * mt * clamped * (p2.x - p1.x) + 3 * clamped * clamped * (p3.x - p2.x),
      y: 3 * mt * mt * (p1.y - p0.y) + 6 * mt * clamped * (p2.y - p1.y) + 3 * clamped * clamped * (p3.y - p2.y),
    };
  }
  const last = points.length - 2;
  return { x: points[last] - points[0], y: points[last + 1] - points[1] };
}

function normalAt(points: number[], t: number): PathPoint {
  const tangent = pathTangentAt(points, t);
  const len = Math.hypot(tangent.x, tangent.y) || 1;
  return { x: -tangent.y / len, y: tangent.x / len };
}

function offsetPathD(points: number[], offset: number, routingType?: string): string {
  const normal = normalAt(points, 0.5);
  if (routingType === 'bezier' && points.length >= 8) {
    const p0 = { x: points[0] + normal.x * offset, y: points[1] + normal.y * offset };
    const p1 = { x: points[2] + normal.x * offset, y: points[3] + normal.y * offset };
    const p2 = { x: points[4] + normal.x * offset, y: points[5] + normal.y * offset };
    const p3 = { x: points[6] + normal.x * offset, y: points[7] + normal.y * offset };
    return `M ${p0.x} ${p0.y} C ${p1.x} ${p1.y} ${p2.x} ${p2.y} ${p3.x} ${p3.y}`;
  }

  const shifted: number[] = [];
  for (let i = 0; i < points.length; i += 2) {
    shifted.push(points[i] + normal.x * offset, points[i + 1] + normal.y * offset);
  }
  return polylineD(shifted, routingType);
}

function pathLengthEstimate(points: number[]): number {
  let length = 0;
  let prev = pathPointAt(points, 0);
  for (let i = 1; i <= 24; i += 1) {
    const next = pathPointAt(points, i / 24);
    length += Math.hypot(next.x - prev.x, next.y - prev.y);
    prev = next;
  }
  return length;
}

function renderDoublePath(
  points: number[],
  color: string,
  width: number,
  routingType: string | undefined,
  dash?: string,
): string {
  const gap = Math.max(4, width + 2);
  const dashAttr = dash ? ` stroke-dasharray="${dash}"` : '';
  return `<path d="${offsetPathD(points, -gap / 2, routingType)} ${offsetPathD(points, gap / 2, routingType)}" fill="none" stroke="${color}" stroke-width="${width}"${dashAttr} />`;
}

function renderZigzagPath(points: number[], color: string, width: number, amplitude = 5): string {
  const length = pathLengthEstimate(points);
  const steps = Math.max(20, Math.floor(length / 5));
  const fadeZone = Math.min(0.15, 15 / Math.max(1, length));
  let d = '';

  for (let i = 0; i <= steps; i += 1) {
    const t = i / steps;
    const base = pathPointAt(points, t);
    const normal = normalAt(points, t);
    let amp = amplitude;
    if (t < fadeZone) {
      amp = amplitude * (t / fadeZone);
    } else if (t > 1 - fadeZone) {
      amp = amplitude * ((1 - t) / fadeZone);
    }
    const offset = (i % 2 === 0 ? 1 : -1) * amp;
    const x = base.x + normal.x * offset;
    const y = base.y + normal.y * offset;
    d += i === 0 ? `M ${x} ${y}` : ` L ${x} ${y}`;
  }

  return `<path d="${d}" fill="none" stroke="${color}" stroke-width="${width}" />`;
}

function renderInLoveMarker(points: number[], color: string, width: number, background: string): string {
  const p = pathPointAt(points, 0.5);
  const tangent = pathTangentAt(points, 0.5);
  const len = Math.hypot(tangent.x, tangent.y) || 1;
  const ux = tangent.x / len;
  const uy = tangent.y / len;
  const r = 6;
  const offset = 4;
  const c1 = { x: p.x - ux * offset, y: p.y - uy * offset };
  const c2 = { x: p.x + ux * offset, y: p.y + uy * offset };
  return `<circle cx="${c1.x}" cy="${c1.y}" r="${r}" fill="${background}" stroke="${color}" stroke-width="${width}" /><circle cx="${c2.x}" cy="${c2.y}" r="${r}" fill="${background}" stroke="${color}" stroke-width="${width}" />`;
}

function renderMidCircle(points: number[], color: string, width: number, background: string): string {
  const p = pathPointAt(points, 0.5);
  return `<circle cx="${p.x}" cy="${p.y}" r="6" fill="${background}" stroke="${color}" stroke-width="${width}" />`;
}

function renderCircleChain(points: number[], color: string, width: number): string {
  const length = pathLengthEstimate(points);
  const radius = 5;
  const count = Math.max(2, Math.floor(length / (radius * 2)));
  let circles = '';
  for (let i = 0; i < count; i += 1) {
    const p = pathPointAt(points, (i + 0.5) / count);
    circles += `<circle cx="${p.x}" cy="${p.y}" r="${radius}" fill="transparent" stroke="${color}" stroke-width="${width}" />`;
  }
  return circles;
}

function renderNeverMetMarker(points: number[], color: string, width: number, background: string): string {
  const p = pathPointAt(points, 0.5);
  const size = 8;
  const square = `<rect x="${p.x - size}" y="${p.y - size}" width="${size * 2}" height="${size * 2}" fill="${background}" stroke="${color}" stroke-width="${width}" />`;
  const xMark = `<path d="M ${p.x - size} ${p.y - size} L ${p.x + size} ${p.y + size} M ${p.x + size} ${p.y - size} L ${p.x - size} ${p.y + size}" stroke="${color}" stroke-width="${width}" />`;
  return `${square}${xMark}`;
}

function renderCutoffRepairedMarker(points: number[], color: string, width: number): string {
  const p = pathPointAt(points, 0.5);
  const tangent = pathTangentAt(points, 0.5);
  const len = Math.hypot(tangent.x, tangent.y) || 1;
  const ux = tangent.x / len;
  const uy = tangent.y / len;
  const nx = -uy;
  const ny = ux;
  const tickCenters = [-6, 6].map((distance) => ({
    x: p.x + ux * distance,
    y: p.y + uy * distance,
  }));
  const ticks = tickCenters
    .map((center) => `<path d="M ${center.x - nx * 6} ${center.y - ny * 6} L ${center.x + nx * 6} ${center.y + ny * 6}" stroke="${color}" stroke-width="${width}" />`)
    .join('');
  return `${ticks}<circle cx="${p.x}" cy="${p.y}" r="4" fill="none" stroke="${color}" stroke-width="${width}" />`;
}

function renderCenterX(points: number[], color: string, width: number, size = 6): string {
  const p = pathPointAt(points, 0.5);
  const tangent = pathTangentAt(points, 0.5);
  const len = Math.hypot(tangent.x, tangent.y) || 1;
  const ux = tangent.x / len;
  const uy = tangent.y / len;
  const nx = -uy;
  const ny = ux;
  const x1 = p.x + (ux + nx) * size;
  const y1 = p.y + (uy + ny) * size;
  const x2 = p.x - (ux + nx) * size;
  const y2 = p.y - (uy + ny) * size;
  const x3 = p.x + (ux - nx) * size;
  const y3 = p.y + (uy - ny) * size;
  const x4 = p.x - (ux - nx) * size;
  const y4 = p.y - (uy - ny) * size;
  return `<path d="M ${x1} ${y1} L ${x2} ${y2} M ${x3} ${y3} L ${x4} ${y4}" stroke="${color}" stroke-width="${width}" />`;
}

function renderCenterSquareX(points: number[], color: string, width: number, background: string): string {
  const p = pathPointAt(points, 0.5);
  const tangent = pathTangentAt(points, 0.5);
  const len = Math.hypot(tangent.x, tangent.y) || 1;
  const ux = tangent.x / len;
  const uy = tangent.y / len;
  const nx = -uy;
  const ny = ux;
  const size = 7;
  const c1 = { x: p.x + (ux + nx) * size, y: p.y + (uy + ny) * size };
  const c2 = { x: p.x + (ux - nx) * size, y: p.y + (uy - ny) * size };
  const c3 = { x: p.x - (ux + nx) * size, y: p.y - (uy + ny) * size };
  const c4 = { x: p.x - (ux - nx) * size, y: p.y - (uy - ny) * size };
  const square = `<path d="M ${c1.x} ${c1.y} L ${c2.x} ${c2.y} L ${c3.x} ${c3.y} L ${c4.x} ${c4.y} Z" fill="${background}" stroke="${color}" stroke-width="${width}" />`;
  const xMark = `<path d="M ${c1.x} ${c1.y} L ${c3.x} ${c3.y} M ${c2.x} ${c2.y} L ${c4.x} ${c4.y}" stroke="${color}" stroke-width="${width}" />`;
  return `${square}${xMark}`;
}

function renderCenterDiamond(points: number[], color: string, width: number, background: string): string {
  const p = pathPointAt(points, 0.5);
  const tangent = pathTangentAt(points, 0.5);
  const len = Math.hypot(tangent.x, tangent.y) || 1;
  const ux = tangent.x / len;
  const uy = tangent.y / len;
  const nx = -uy;
  const ny = ux;
  const size = 8;
  const top = { x: p.x + nx * size, y: p.y + ny * size };
  const right = { x: p.x + ux * size, y: p.y + uy * size };
  const bottom = { x: p.x - nx * size, y: p.y - ny * size };
  const left = { x: p.x - ux * size, y: p.y - uy * size };
  return `<path d="M ${top.x} ${top.y} L ${right.x} ${right.y} L ${bottom.x} ${bottom.y} L ${left.x} ${left.y} Z" fill="${background}" stroke="${color}" stroke-width="${width}" />`;
}

function renderEndTriangle(points: number[], color: string, width: number, background: string, filled: boolean): string {
  const end = pathPointAt(points, 1);
  const tangent = pathTangentAt(points, 1);
  const len = Math.hypot(tangent.x, tangent.y) || 1;
  const ux = tangent.x / len;
  const uy = tangent.y / len;
  const nx = -uy;
  const ny = ux;
  const tip = { x: end.x - ux * 2, y: end.y - uy * 2 };
  const base = { x: tip.x - ux * 12, y: tip.y - uy * 12 };
  const left = { x: base.x + nx * 8, y: base.y + ny * 8 };
  const right = { x: base.x - nx * 8, y: base.y - ny * 8 };
  return `<path d="M ${tip.x} ${tip.y} L ${left.x} ${left.y} L ${right.x} ${right.y} Z" fill="${filled ? color : background}" stroke="${color}" stroke-width="${width}" />`;
}

function renderEndChevron(points: number[], color: string, width: number): string {
  const end = pathPointAt(points, 1);
  const tangent = pathTangentAt(points, 1);
  const len = Math.hypot(tangent.x, tangent.y) || 1;
  const ux = tangent.x / len;
  const uy = tangent.y / len;
  const nx = -uy;
  const ny = ux;
  const tip = { x: end.x - ux * 2, y: end.y - uy * 2 };
  const base = { x: tip.x - ux * 12, y: tip.y - uy * 12 };
  const left = { x: base.x + nx * 8, y: base.y + ny * 8 };
  const right = { x: base.x - nx * 8, y: base.y - ny * 8 };
  return `<path d="M ${left.x} ${left.y} L ${tip.x} ${tip.y} L ${right.x} ${right.y}" fill="none" stroke="${color}" stroke-width="${width}" />`;
}

function renderDistrustTicks(points: number[]): string {
  const count = Math.max(5, Math.min(35, Math.floor(pathLengthEstimate(points) / 12)));
  let ticks = '';
  for (let i = 0; i < count; i += 1) {
    const p = pathPointAt(points, (i + 1) / (count + 1));
    const normal = normalAt(points, (i + 1) / (count + 1));
    ticks += `<path d="M ${p.x - normal.x * 7} ${p.y - normal.y * 7} L ${p.x + normal.x * 7} ${p.y + normal.y * 7}" stroke="#CA3A31" stroke-width="4" />`;
  }
  return ticks;
}

function normalizeConnectorLabel(value: unknown): string {
  return typeof value === 'string'
    ? value.trim().toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '')
    : '';
}

function renderBestFriendsTicks(points: number[], color: string, width: number): string {
  const count = Math.max(5, Math.min(35, Math.floor(pathLengthEstimate(points) / 12)));
  let ticks = '';
  for (let i = 0; i < count; i += 1) {
    const t = (i + 1) / (count + 1);
    const p = pathPointAt(points, t);
    const normal = normalAt(points, t);
    ticks += `<path d="M ${p.x - normal.x * 7.5} ${p.y - normal.y * 7.5} L ${p.x + normal.x * 7.5} ${p.y + normal.y * 7.5}" stroke="${color}" stroke-width="${width}" />`;
  }
  return ticks;
}

function coerceBoolean(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'object' && value !== null && typeof (value as any).valueOf === 'function') {
    return coerceBoolean((value as any).valueOf());
  }
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', 'yes', '1'].includes(normalized)) return true;
  }
  return false;
}

function coerceNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const num = Number(value);
    if (Number.isFinite(num)) return num;
  }
  return undefined;
}

function coerceDashArray(value: unknown): string | undefined {
  if (!value) return undefined;
  if (Array.isArray(value)) {
    const parts = value
      .map((entry) => {
        const num = coerceNumber(entry);
        return typeof num === 'number' ? num : undefined;
      })
      .filter((entry): entry is number => typeof entry === 'number');
    return parts.length ? parts.join(',') : undefined;
  }
  if (typeof value === 'string') {
    const parts = value
      .split(/[ ,]+/)
      .map((segment) => Number(segment))
      .filter((num) => Number.isFinite(num));
    return parts.length ? parts.join(',') : undefined;
  }
  return undefined;
}

function resolveLineStrokeDash(props: Record<string, unknown>): string | undefined {
  const dashArray = coerceDashArray(props.dash ?? props.dashPattern);
  if (props.dashDoubleDot === true) return dashArray ?? '12,4,2,4,2,4';
  if (props.dashSingleDot === true) return dashArray ?? '12,8,2,8';
  if (props.dashDot === true) return dashArray ?? '12,4,2,4';
  if (props.dotted === true) return dashArray ?? '2,4';
  if (props.dashed === true) return dashArray ?? '6,6';
  return dashArray;
}

function safeMarkerIdPart(value: unknown): string {
  return String(value ?? 'connector').replace(/[^a-zA-Z0-9_-]/g, '-');
}

function resolveArrowMarker(
  props: Record<string, unknown>,
  key: 'startArrow' | 'endArrow',
  fallbackSize: number,
): { type: string; size: number } | null {
  const arrow = props[key];
  if (!arrow || typeof arrow !== 'object') return null;
  const record = arrow as Record<string, unknown>;
  const type = typeof record.type === 'string' ? record.type : 'none';
  if (type === 'none') return null;
  return {
    type,
    size: coerceNumber(record.size) ?? fallbackSize,
  };
}

function trimPolylineEndpoint(points: number[], end: 'start' | 'end', amount: number): number[] {
  if (points.length < 4 || amount <= 0) return points;
  const next = [...points];
  const tipIndex = end === 'end' ? points.length - 2 : 0;
  const neighborIndex = end === 'end' ? points.length - 4 : 2;
  const tipX = points[tipIndex] ?? 0;
  const tipY = points[tipIndex + 1] ?? 0;
  const neighborX = points[neighborIndex] ?? tipX;
  const neighborY = points[neighborIndex + 1] ?? tipY;
  const dx = tipX - neighborX;
  const dy = tipY - neighborY;
  const len = Math.hypot(dx, dy);
  if (len <= 0) return next;
  const trim = Math.min(amount, len);
  const ux = dx / len;
  const uy = dy / len;
  if (end === 'end') {
    next[tipIndex] = tipX - ux * trim;
    next[tipIndex + 1] = tipY - uy * trim;
  } else {
    next[tipIndex] = tipX + ux * trim;
    next[tipIndex + 1] = tipY + uy * trim;
  }
  return next;
}

function renderCanvasTriangleArrow(points: number[], end: 'start' | 'end', color: string): string {
  if (points.length < 4) return '';
  const tipIndex = end === 'end' ? points.length - 2 : 0;
  const neighborIndex = end === 'end' ? points.length - 4 : 2;
  const tipX = points[tipIndex] ?? 0;
  const tipY = points[tipIndex + 1] ?? 0;
  const neighborX = points[neighborIndex] ?? tipX;
  const neighborY = points[neighborIndex + 1] ?? tipY;
  const dx = end === 'end' ? tipX - neighborX : neighborX - tipX;
  const dy = end === 'end' ? tipY - neighborY : neighborY - tipY;
  const len = Math.hypot(dx, dy);
  if (len <= 0) return '';
  const ux = dx / len;
  const uy = dy / len;
  const baseX = end === 'end' ? tipX - ux * 12 : tipX + ux * 12;
  const baseY = end === 'end' ? tipY - uy * 12 : tipY + uy * 12;
  const px = -uy * 4;
  const py = ux * 4;
  return `<path d="M ${tipX} ${tipY} L ${baseX + px} ${baseY + py} L ${baseX - px} ${baseY - py} Z" fill="${color}" stroke="${color}" stroke-width="1" />`;
}

function resolveLineArrowExport(
  props: Record<string, unknown>,
  points: number[],
  color: string,
): { defs: string; attrs: string; arrowSvg: string; points: number[] } {
  const connectorId = safeMarkerIdPart(props.__exportConnectorId);
  const start = resolveArrowMarker(props, 'startArrow', 6);
  const end = resolveArrowMarker(props, 'endArrow', 8);
  const markers: string[] = [];
  const attrs: string[] = [];
  const arrowSvg: string[] = [];
  let pathPoints = points;

  if (start) {
    if (start.type === 'triangle') {
      pathPoints = trimPolylineEndpoint(pathPoints, 'start', 12);
      arrowSvg.push(renderCanvasTriangleArrow(points, 'start', color));
    } else {
      const id = `arrow-start-${connectorId}-${Math.abs(Math.round(points[0] ?? 0))}-${Math.abs(Math.round(points[1] ?? 0))}`;
      markers.push(svgArrowMarker(id, color, start.size, start.type));
      attrs.push(`marker-start="url(#${id})"`);
    }
  }
  if (end) {
    if (end.type === 'triangle') {
      pathPoints = trimPolylineEndpoint(pathPoints, 'end', 12);
      arrowSvg.push(renderCanvasTriangleArrow(points, 'end', color));
    } else {
      const id = `arrow-end-${connectorId}-${Math.abs(Math.round(points[points.length - 2] ?? 0))}-${Math.abs(Math.round(points[points.length - 1] ?? 0))}`;
      markers.push(svgArrowMarker(id, color, end.size, end.type));
      attrs.push(`marker-end="url(#${id})"`);
    }
  }

  return {
    defs: markers.length ? `<defs>${markers.join('')}</defs>` : '',
    attrs: attrs.length ? ` ${attrs.join(' ')}` : '',
    arrowSvg: arrowSvg.join(''),
    points: pathPoints,
  };
}

function findHorizontalSegment(points: number[]): { x1: number; y: number; x2: number } | null {
  let best: { x1: number; y: number; x2: number } | null = null;
  let bestLength = 0;
  for (let i = 0; i <= points.length - 4; i += 2) {
    const x1 = points[i];
    const y1 = points[i + 1];
    const x2 = points[i + 2];
    const y2 = points[i + 3];
    if (Math.abs(y1 - y2) < 0.5) {
      const length = Math.abs(x2 - x1);
      if (length > bestLength) {
        bestLength = length;
        best = {
        x1,
        y: (y1 + y2) / 2,
        x2,
      };
      }
    }
  }
  return best;
}

function houseMarkerOnHorizontalSegment(
  horizontal: { x1: number; y: number; x2: number },
  color: string,
  strokeWidth: number,
): string {
  const midX = (horizontal.x1 + horizontal.x2) / 2;
  const y = horizontal.y;
  const w = 10;
  const houseHeight = 8;
  const roofHeight = 6;
  const half = (houseHeight + roofHeight) / 2;
  const d = `M ${midX - w / 2} ${y + half} L ${midX - w / 2} ${y - half + roofHeight} L ${midX} ${y - half} L ${midX + w / 2} ${y - half + roofHeight} L ${midX + w / 2} ${y + half} Z`;
  return `<path d="${d}" fill="#ffffff" stroke="${color}" stroke-width="${strokeWidth}" stroke-linejoin="round" />`;
}

type MarriageVariantProps = MarriageProps & {
};

type LineVariantProps = LineProps & {
};

function deriveMarriageLabel(props: MarriageVariantProps): string {
  const tr = (key: string, en: string) => i18n.t(`genogram:relationships.${key}`, { defaultValue: en }) as string;
  const relation = typeof props.relation === 'string' ? props.relation.toLowerCase() : 'marriage';
  if (coerceBoolean(props.divorcedV2) || relation === 'divorce_v2') {
    return i18n.t('diagram:relationships.subtypes.divorce_v2', { defaultValue: 'Divorced' }) as string;
  }
  if (coerceBoolean(props.divorced) || relation === 'divorce') {
    return tr('divorce', 'Divorce');
  }
  if (coerceBoolean(props.nullity) || relation === 'nullity') return tr('nullity', 'Nullity');
  if (coerceBoolean(props.separated) || relation === 'separation') return tr('separation', 'Separation');
  if (coerceBoolean(props.engaged) || relation === 'engagement') return tr('engagement', 'Engagement');
  if (coerceBoolean(props.house) || relation === 'cohabitation') return tr('cohabitation', 'Cohabitation');
  return tr('marriage', 'Marriage');
}

export const LineConnectorManifest: ConnectorManifest<LineProps> = {
  id: 'line',
  version: '1.0.0',
  category: 'genogram',
  label: 'Relationship',
  iconId: 'line',
  propsSchema: lineProps,
  defaultProps: () => ({ color: '#334155', width: 2, dashed: false, endArrow: { type: 'triangle', size: 8 }, startArrow: { type: 'none', size: 6 } }),
  svg: ({ points, props }) => {
    if (!points || points.length < 4) return '';
    const lineProps = props as LineVariantProps & {
      doubleLine?: unknown;
      tripleLine?: unknown;
      bestFriends?: unknown;
      midTicks?: unknown;
      fusedHostile?: unknown;
      inLoveHarmony?: unknown;
      midCircle?: unknown;
      cutoffRepaired?: unknown;
      neverMet?: unknown;
      emotionalConnection?: unknown;
      wave?: unknown;
      distanceHostile?: unknown;
      closeHostile?: unknown;
      distrust?: unknown;
      manipulative?: unknown;
      controlling?: unknown;
      jealous?: unknown;
      fanAdmirer?: unknown;
      limerence?: unknown;
      abusePattern?: unknown;
      physicalAbuse?: unknown;
      emotionalAbuse?: unknown;
      sexualAbuse?: unknown;
      neglectAbuse?: unknown;
      distantViolence?: unknown;
      closeViolence?: unknown;
      focusedNegatively?: unknown;
      relation?: unknown;
      __exportBackground?: unknown;
    };

    const color = typeof lineProps.color === 'string' ? lineProps.color : '#334155';
    const width = coerceNumber(lineProps.width) ?? 2;
    const dash = lineProps.dash
      ? coerceDashArray(lineProps.dash)
      : lineProps.dashed
        ? '6,6'
        : undefined;

    const markers: string[] = [];
    let markerStart = '';
    let markerEnd = '';

    if (lineProps.startArrow?.type && lineProps.startArrow.type !== 'none') {
      const id = `arrow-start-${Math.abs(Math.round(points[0]))}-${Math.abs(Math.round(points[1]))}`;
      markers.push(svgArrowMarker(id, color, coerceNumber(lineProps.startArrow.size) ?? 6, lineProps.startArrow.type));
      markerStart = ` marker-start="url(#${id})"`;
    }

    if (lineProps.endArrow?.type && lineProps.endArrow.type !== 'none') {
      const id = `arrow-end-${Math.abs(Math.round(points[points.length - 2]))}-${Math.abs(Math.round(points[points.length - 1]))}`;
      markers.push(svgArrowMarker(id, color, coerceNumber(lineProps.endArrow.size) ?? 8, lineProps.endArrow.type));
      markerEnd = ` marker-end="url(#${id})"`;
    }

    const defs = markers.length ? `<defs>${markers.join('')}</defs>` : '';
    const routingType = lineProps.routingType || 'straight';

    const sampleBezier = (
      p: number[],
      t: number,
    ): { x: number; y: number } => {
      const mt = 1 - t;
      return {
        x:
          mt * mt * mt * p[0] +
          3 * mt * mt * t * p[2] +
          3 * mt * t * t * p[4] +
          t * t * t * p[6],
        y:
          mt * mt * mt * p[1] +
          3 * mt * mt * t * p[3] +
          3 * mt * t * t * p[5] +
          t * t * t * p[7],
      };
    };

    const sampleBezierTangent = (
      p: number[],
      t: number,
    ): { dx: number; dy: number } => {
      const mt = 1 - t;
      return {
        dx:
          3 * mt * mt * (p[2] - p[0]) +
          6 * mt * t * (p[4] - p[2]) +
          3 * t * t * (p[6] - p[4]),
        dy:
          3 * mt * mt * (p[3] - p[1]) +
          6 * mt * t * (p[5] - p[3]) +
          3 * t * t * (p[7] - p[5]),
      };
    };

    const offsetBezierPoints = (p: number[], offset: number): number[] => {
      if (p.length < 8 || offset === 0) return p;
      const tangent = sampleBezierTangent(p, 0.5);
      const len = Math.hypot(tangent.dx, tangent.dy) || 1;
      const nx = -tangent.dy / len;
      const ny = tangent.dx / len;
      return [
        p[0] + nx * offset,
        p[1] + ny * offset,
        p[2] + nx * offset,
        p[3] + ny * offset,
        p[4] + nx * offset,
        p[5] + ny * offset,
        p[6] + nx * offset,
        p[7] + ny * offset,
      ];
    };

    const isBezier = routingType === 'bezier' && points.length >= 8;
    const lineStyleFlags = lineProps as LineVariantProps & { doubleLine?: boolean; tripleLine?: boolean };
    const lineOffsets = lineStyleFlags.tripleLine === true ? [-4, 0, 4] : lineStyleFlags.doubleLine === true ? [-3.5, 3.5] : [0];
    const path = lineOffsets
      .map((offset) => polylineD(isBezier ? offsetBezierPoints(points, offset) : points, routingType))
      .filter((d) => d.length > 0)
      .join(' ');

    const tangentAtMid = isBezier
      ? sampleBezierTangent(points, 0.5)
      : {
          dx: points[points.length - 2] - points[0],
          dy: points[points.length - 1] - points[1],
        };
    const midpoint = isBezier
      ? sampleBezier(points, 0.5)
      : { x: (points[0] + points[points.length - 2]) / 2, y: (points[1] + points[points.length - 1]) / 2 };
    const tLen = Math.hypot(tangentAtMid.dx, tangentAtMid.dy) || 1;
    const tux = tangentAtMid.dx / tLen;
    const tuy = tangentAtMid.dy / tLen;
    const nux = -tuy;
    const nuy = tux;

    let overlays = '';
    const styleFlags = lineProps as LineVariantProps & { midTicks?: boolean; cutoffRepaired?: boolean };
    if (styleFlags.midTicks === true) {
      const tickLen = 12;
      const tickHalf = tickLen / 2;
      const tickCenters = [-2, 2].map((d) => ({ x: midpoint.x + tux * d, y: midpoint.y + tuy * d }));
      overlays += tickCenters
        .map((c) => `<path d="M ${c.x - nux * tickHalf} ${c.y - nuy * tickHalf} L ${c.x + nux * tickHalf} ${c.y + nuy * tickHalf}" stroke="${color}" stroke-width="${width}" />`)
        .join('');
    }
    if (styleFlags.cutoffRepaired === true) {
      const tickOffset = 6;
      const tickHalf = 6;
      const tick1 = { x: midpoint.x - tux * tickOffset, y: midpoint.y - tuy * tickOffset };
      const tick2 = { x: midpoint.x + tux * tickOffset, y: midpoint.y + tuy * tickOffset };
      overlays += `<circle cx="${midpoint.x}" cy="${midpoint.y}" r="4" fill="#ffffff" stroke="${color}" stroke-width="${width}" />`;
      overlays += `<path d="M ${tick1.x - nux * tickHalf} ${tick1.y - nuy * tickHalf} L ${tick1.x + nux * tickHalf} ${tick1.y + nuy * tickHalf}" stroke="${color}" stroke-width="${width}" />`;
      overlays += `<path d="M ${tick2.x - nux * tickHalf} ${tick2.y - nuy * tickHalf} L ${tick2.x + nux * tickHalf} ${tick2.y + nuy * tickHalf}" stroke="${color}" stroke-width="${width}" />`;
    }

    const background =
      typeof lineProps.__exportBackground === 'string' && lineProps.__exportBackground.trim().length > 0
        ? lineProps.__exportBackground
        : EXPORT_LABEL_BACKGROUND;
    const labelOffsetRaw = coerceNumber((lineProps as Record<string, unknown>).labelOffset) ?? 0.5;
    const labelOffset = Math.max(0, Math.min(1, labelOffsetRaw));
    const labelText = typeof lineProps.label === 'string' ? lineProps.label.trim() : '';
    const labelPoint = isBezier
      ? (() => {
          const point = sampleBezier(points, labelOffset);
          return {
            x: point.x,
            y: point.y - resolveBezierExportLabelYOffset(lineProps as Record<string, unknown>) - EXPORT_LABEL_VISUAL_NUDGE_Y,
          };
        })()
      : midpoint;
    const returnWithLabel = (connectorBody: string): string => {
      if (!labelText) return `${defs}${connectorBody}`;
      const labeled = finalizeConnectorExportWithLabel(defs, connectorBody, {
        x: labelPoint.x,
        y: labelPoint.y,
        text: labelText,
        strokeWidth: width,
        isBezier,
        props: lineProps as Record<string, unknown>,
        connectorId: typeof (lineProps as Record<string, unknown>).__exportConnectorId === 'string'
          ? String((lineProps as Record<string, unknown>).__exportConnectorId)
          : undefined,
      });
      const propsRecord = props as Record<string, unknown>;
      propsRecord.__exportLabelSvg = labeled.labelSvg;
      propsRecord.__exportLabelMaskBounds = labeled.bounds;
      return labeled.geometrySvg;
    };
    (props as Record<string, unknown>).__exportLabelHandled = Boolean(labelText);

    if (coerceBoolean(lineProps.emotionalConnection)) {
      return returnWithLabel(renderCircleChain(points, color, width));
    }

    if (coerceBoolean(lineProps.distanceHostile)) {
      return returnWithLabel(`<path d="${path}" fill="none" stroke="#000000" stroke-width="${width}" stroke-dasharray="6,6" />${renderZigzagPath(points, '#DC0101', width, 3)}`);
    }

    if (coerceBoolean(lineProps.closeHostile)) {
      const gap = Math.max(4, width + 2);
      return returnWithLabel(`<path d="${offsetPathD(points, -gap / 2, routingType)} ${offsetPathD(points, gap / 2, routingType)}" fill="none" stroke="#000000" stroke-width="${width}" />${renderZigzagPath(points, '#DC0101', width, 5)}`);
    }

    if (coerceBoolean(lineProps.distantViolence)) {
      return returnWithLabel(`<path d="${path}" fill="none" stroke="#000000" stroke-width="${width}" stroke-dasharray="6,6" />${renderZigzagPath(points, '#DC0101', Math.max(1, width - 1), 3)}`);
    }

    if (coerceBoolean(lineProps.closeViolence)) {
      const gap = Math.max(4, width + 2);
      return returnWithLabel(`<path d="${offsetPathD(points, -gap / 2, routingType)} ${offsetPathD(points, gap / 2, routingType)}" fill="none" stroke="#000000" stroke-width="${width}" />${renderZigzagPath(points, '#DC0101', width, 5)}`);
    }

    if (coerceBoolean(lineProps.fusedHostile)) {
      const gap = Math.max(4, width + 1);
      const strokes = [-gap, 0, gap]
        .map((offset) => `<path d="${offsetPathD(points, offset, routingType)}" fill="none" stroke="#000000" stroke-width="${width}" />`)
        .join('');
      return returnWithLabel(`${strokes}${renderZigzagPath(points, '#DC0101', width, 5)}`);
    }

    if (coerceBoolean(lineProps.tripleLine)) {
      const gap = Math.max(4, width + 1);
      return returnWithLabel(`${[-gap, 0, gap]
        .map((offset) => `<path d="${offsetPathD(points, offset, routingType)}" fill="none" stroke="${color}" stroke-width="${width}"${dash ? ` stroke-dasharray="${dash}"` : ''} />`)
        .join('')}`);
    }

    if (coerceBoolean(lineProps.doubleLine)) {
      const labelKey = normalizeConnectorLabel(labelText || lineProps.relation);
      const shouldRenderBestFriendTicks =
        coerceBoolean(lineProps.bestFriends) ||
        labelKey === 'bestfriendsveryclose' ||
        labelKey === 'bestfriends' ||
        labelKey === 'veryclose';
      const ticks = shouldRenderBestFriendTicks ? renderBestFriendsTicks(points, color, width) : '';
      return returnWithLabel(`${renderDoublePath(points, color, width, routingType, dash)}${ticks}`);
    }

    if (coerceBoolean(lineProps.midTicks)) {
      const tangent = pathTangentAt(points, 0.5);
      const len = Math.hypot(tangent.x, tangent.y) || 1;
      const ux = tangent.x / len;
      const uy = tangent.y / len;
      const nx = -uy;
      const ny = ux;
      const mid = pathPointAt(points, 0.5);
      const tickCenters = [-2, 2].map((distance) => ({
        x: mid.x + ux * distance,
        y: mid.y + uy * distance,
      }));
      const ticks = tickCenters
        .map((center) => `<path d="M ${center.x - nx * 6} ${center.y - ny * 6} L ${center.x + nx * 6} ${center.y + ny * 6}" stroke="${color}" stroke-width="${width}" />`)
        .join('');
      return returnWithLabel(`<path d="${path}" fill="none" stroke="${color}" stroke-width="${width}"${dash ? ` stroke-dasharray="${dash}"` : ''} />${ticks}`);
    }

    if (coerceBoolean(lineProps.cutoffRepaired)) {
      return returnWithLabel(`<path d="${path}" fill="none" stroke="${color}" stroke-width="${width}"${dash ? ` stroke-dasharray="${dash}"` : ' stroke-dasharray="6,6"'} />${renderCutoffRepairedMarker(points, color, width)}`);
    }

    if (coerceBoolean(lineProps.neverMet)) {
      return returnWithLabel(`<path d="${path}" fill="none" stroke="${color}" stroke-width="${width}" />${renderNeverMetMarker(points, color, width, background)}`);
    }

    if (coerceBoolean(lineProps.distrust)) {
      return returnWithLabel(`<path d="${path}" fill="none" stroke="#000000" stroke-width="${width}" />${renderDistrustTicks(points)}`);
    }

    if (coerceBoolean(lineProps.wave)) {
      return returnWithLabel(`${renderZigzagPath(points, color, width, 3)}`);
    }

    if (coerceBoolean(lineProps.focusedNegatively)) {
      return returnWithLabel(`<path d="${path}" fill="none" stroke="${color}" stroke-width="${width}" />${renderZigzagPath(points, color, width, 6)}${renderEndChevron(points, color, width)}`);
    }

    if (coerceBoolean(lineProps.abusePattern)) {
      const abuseWidth = Math.max(1, width - 1);
      return returnWithLabel(`<path d="${path}" fill="none" stroke="#000000" stroke-width="${width}" stroke-dasharray="6,6" />${renderZigzagPath(points, '#2563eb', abuseWidth, 3)}${renderEndTriangle(points, '#2563eb', abuseWidth, background, false)}`);
    }

    if (coerceBoolean(lineProps.physicalAbuse)) {
      return returnWithLabel(`${renderZigzagPath(points, '#2563eb', width, 3)}${renderEndTriangle(points, '#2563eb', width, background, true)}`);
    }

    if (coerceBoolean(lineProps.emotionalAbuse)) {
      return returnWithLabel(`${renderZigzagPath(points, '#2563eb', width, 3)}${renderEndTriangle(points, '#2563eb', width, background, false)}`);
    }

    if (coerceBoolean(lineProps.sexualAbuse)) {
      const gap = 3.5;
      return returnWithLabel(`<path d="${offsetPathD(points, -gap, routingType)} ${offsetPathD(points, gap, routingType)}" fill="none" stroke="#2563eb" stroke-width="${width}" />${renderZigzagPath(points, '#2563eb', width, 5)}${renderEndTriangle(points, '#2563eb', width, background, true)}`);
    }

    if (coerceBoolean(lineProps.neglectAbuse)) {
      return returnWithLabel(`<path d="${path}" fill="none" stroke="${color}" stroke-width="${width}" stroke-dasharray="${dash ?? '6,6'}" />${renderEndChevron(points, '#2563eb', width)}`);
    }

    if (coerceBoolean(lineProps.manipulative)) {
      return returnWithLabel(`<path d="${path}" fill="none" stroke="${color}" stroke-width="${width}"${markerEnd} />${renderCenterX(points, color, width)}`);
    }

    if (coerceBoolean(lineProps.controlling)) {
      return returnWithLabel(`<path d="${path}" fill="none" stroke="${color}" stroke-width="${width}"${markerEnd} />${renderCenterSquareX(points, color, width, background)}`);
    }

    if (coerceBoolean(lineProps.jealous)) {
      return returnWithLabel(`<path d="${path}" fill="none" stroke="${color}" stroke-width="${width}"${markerEnd} />${renderCenterDiamond(points, color, width, background)}`);
    }

    if (coerceBoolean(lineProps.fanAdmirer)) {
      return returnWithLabel(`<path d="${path}" fill="none" stroke="${color}" stroke-width="${width}"${markerEnd} />${renderMidCircle(points, color, width, background)}`);
    }

    if (coerceBoolean(lineProps.limerence)) {
      return returnWithLabel(`<path d="${path}" fill="none" stroke="${color}" stroke-width="${width}"${markerEnd} />${renderInLoveMarker(points, color, width, background)}`);
    }

    const marker = coerceBoolean(lineProps.inLoveHarmony)
      ? renderInLoveMarker(points, color, width, background)
      : coerceBoolean(lineProps.midCircle)
        ? renderMidCircle(points, color, width, background)
        : '';

    return returnWithLabel(`<path d="${path}" fill="none" stroke="${color}" stroke-width="${width}"${dash ? ` stroke-dasharray="${dash}"` : ''}${markerStart}${markerEnd} />${overlays}${marker}`);
  },
};

export const MarriageConnectorManifest: ConnectorManifest<MarriageProps> = {
  id: 'marriage',
  version: '1.0.0',
  category: 'genogram',
  label: 'Marriage',
  iconId: 'marriage',
  propsSchema: marriageProps,
  defaultProps: () => ({ color: '#334155', width: 2, dashed: false, endArrow: { type: 'none', size: 0 }, startArrow: { type: 'none', size: 0 }, relation: 'marriage' }),
  konvaNative: marriageKonvaNative,
  svg: ({ points, props }) => {
    if (!points || points.length < 4) return '';
    const flags = props as MarriageProps & {
      divorced?: unknown;
      divorcedV2?: unknown;
      separated?: unknown;
      engaged?: unknown;
      house?: unknown;
      dashDot?: unknown;
      dashDoubleDot?: unknown;
      dashSingleDot?: unknown;
      dotted?: unknown;
      legalCohabLegalSep?: unknown;
      separatedInFact?: unknown;
      cohabSeparation?: unknown;
      nonSentimentalCohabSep?: unknown;
      casualRelSep?: unknown;
      separationInFactSlash?: unknown;
      legalSeparationSlash?: unknown;
      nullity?: unknown;
      solidDashedDouble?: unknown;
      dash?: number[] | string;
      dashPattern?: number[] | string;
    };
    const divorced = coerceBoolean(flags.divorced);
    const divorcedV2 = coerceBoolean(flags.divorcedV2);
    const separated = !divorced && !divorcedV2 && coerceBoolean(flags.separated);
    const engaged = coerceBoolean(flags.engaged);
    const house = coerceBoolean(flags.house);
    const color =
      (props.color as string) ||
      (divorced || divorcedV2 ? '#DC0101' : separated ? '#B45309' : engaged ? '#0EA5E9' : '#334155');
    const overlaySlashColor = '#DC0101';
    const exportBgRaw = (props as Record<string, unknown>).__exportBackground;
    const exportBackground =
      typeof exportBgRaw === 'string' && exportBgRaw.trim().length > 0 ? exportBgRaw.trim() : 'white';
    const width = coerceNumber(props.width) ?? 2;
    const dash =
      coerceDashArray(flags.dash ?? flags.dashPattern) ??
      (coerceBoolean(flags.dashDoubleDot)
        ? '12,4,2,4,2,4'
        : coerceBoolean(flags.dashSingleDot)
          ? '12,8,2,8'
          : coerceBoolean(flags.dashDot)
            ? '12,4,2,4'
            : coerceBoolean(flags.dotted)
              ? '2,4'
              : props.dashed
                ? '6,6'
                : undefined);
    const routingType = (props as any).routingType || 'straight';
    const d = polylineD(points, routingType);
    const horizontal = findHorizontalSegment(points);
    const propsRecord = props as MarriageVariantProps;
    const providedLabel = typeof propsRecord.label === 'string' ? propsRecord.label.trim() : '';
    const computedLabel = deriveMarriageLabel(propsRecord);
    const labelText = providedLabel && providedLabel.toLowerCase() !== 'marriage'
      ? providedLabel
      : computedLabel;
    const labelSvg =
      horizontal && labelText
        ? `<text x="${(horizontal.x1 + horizontal.x2) / 2}" y="${horizontal.y - 6}" text-anchor="middle" font-family="${resolveGenogramExportFontStack(labelText)}" font-size="${EXPORT_MARRIAGE_LABEL_FONT_SIZE}" fill="${EXPORT_MARRIAGE_LABEL_FILL}">${escapeText(labelText)}</text>`
        : '';

    let markers = '';
    if (dash) {
      markers = ` stroke-dasharray="${dash}"`;
    }
    let overlays = '';
    if (horizontal) {
      const midX = (horizontal.x1 + horizontal.x2) / 2;
      const midY = horizontal.y;
      if (coerceBoolean(flags.solidDashedDouble)) {
        const offset = 3.5;
        const startX = points[0];
        const startY = points[1];
        const endX = points[points.length - 2];
        const endY = points[points.length - 1];
        const upperY = midY - offset;
        const lowerY = midY + offset;
        const dashPattern = coerceDashArray(flags.dash ?? flags.dashPattern) ?? '6,6';
        const solidD = `M ${startX} ${startY} L ${startX} ${upperY} L ${horizontal.x2} ${upperY} L ${endX} ${endY}`;
        const dashedD = `M ${horizontal.x1} ${lowerY} L ${horizontal.x2} ${lowerY}`;
        return `<path d="${solidD}" fill="none" stroke="${color}" stroke-width="${width}" /><path d="${dashedD}" fill="none" stroke="${color}" stroke-width="${width}" stroke-dasharray="${dashPattern}" />${labelSvg}`;
      }
      if (coerceBoolean(flags.house)) {
        const houseWidth = 10;
        const houseHeight = 8;
        const roofHeight = 6;
        const halfHouseHeight = (houseHeight + roofHeight) / 2;
        overlays += `<path d="M ${midX - houseWidth / 2} ${midY + halfHouseHeight} L ${midX - houseWidth / 2} ${midY - halfHouseHeight + roofHeight} L ${midX} ${midY - halfHouseHeight} L ${midX + houseWidth / 2} ${midY - halfHouseHeight + roofHeight} L ${midX + houseWidth / 2} ${midY + halfHouseHeight} Z" fill="${exportBackground}" stroke="${color}" stroke-width="${width}" stroke-linejoin="round" />`;
      }
      if (coerceBoolean(flags.nullity)) {
        overlays += `<path d="M ${midX - 6} ${midY - 6} L ${midX - 12} ${midY + 6}" stroke="${color}" stroke-width="${width}" />`;
        overlays += `<path d="M ${midX + 3} ${midY - 6} L ${midX - 3} ${midY + 6}" stroke="${color}" stroke-width="${width}" />`;
        overlays += `<path d="M ${midX + 12} ${midY - 6} L ${midX + 6} ${midY + 6}" stroke="${color}" stroke-width="${width}" />`;
      } else if (coerceBoolean(flags.legalCohabLegalSep) || coerceBoolean(flags.legalSeparationSlash)) {
        overlays += `<path d="M ${midX + 6} ${midY - 6} L ${midX - 6} ${midY + 6}" stroke="${color}" stroke-width="${width}" />`;
      } else if (
        coerceBoolean(flags.separatedInFact) ||
        coerceBoolean(flags.cohabSeparation) ||
        coerceBoolean(flags.nonSentimentalCohabSep) ||
        coerceBoolean(flags.casualRelSep) ||
        coerceBoolean(flags.separationInFactSlash)
      ) {
        overlays += `<path d="M ${midX - 6} ${midY - 6} L ${midX + 6} ${midY + 6}" stroke="${color}" stroke-width="${width}" />`;
      }
      if (!(divorced || divorcedV2 || separated)) {
        return `<path d="${d}" fill="none" stroke="${color}" stroke-width="${width}"${markers} />${overlays}${labelSvg}`;
      }
      const slashLength = 12;
      const offset = slashLength / 2;
      const slashSpacing = slashLength / 1.5;
      const primaryCenter = midX - slashSpacing / 2;
      const secondaryCenter = midX + slashSpacing / 2;
      // Forward slashes (/) for divorcedV2, backward slashes (\) for divorced/separated
      if (divorcedV2) {
        const primary = `<path d="M ${primaryCenter + offset} ${midY - offset} L ${primaryCenter - offset} ${midY + offset}" stroke="${overlaySlashColor}" stroke-width="${width}" />`;
        const secondary = `<path d="M ${secondaryCenter + offset} ${midY - offset} L ${secondaryCenter - offset} ${midY + offset}" stroke="${overlaySlashColor}" stroke-width="${width}" />`;
        overlays += primary + secondary;
      } else {
        const primary = `<path d="M ${primaryCenter - offset} ${midY - offset} L ${primaryCenter + offset} ${midY + offset}" stroke="${overlaySlashColor}" stroke-width="${width}" />`;
        const secondary = divorced
          ? `<path d="M ${secondaryCenter - offset} ${midY - offset} L ${secondaryCenter + offset} ${midY + offset}" stroke="${overlaySlashColor}" stroke-width="${width}" />`
          : '';
        overlays += primary + secondary;
      }
    }

    let houseSvg = '';
    if (horizontal && house && !divorced && !divorcedV2 && !separated) {
      houseSvg = houseMarkerOnHorizontalSegment(horizontal, color, width);
    }

    return {
      svg: `<path d="${d}" fill="none" stroke="${color}" stroke-width="${width}"${markers} />${overlays}${houseSvg}${labelSvg}`,
      labelHandled: Boolean(labelText),
      label: labelText || undefined,
    };
  }
};

export const ChildConnectorManifest: ConnectorManifest<ChildProps> = {
  id: 'child',
  version: '1.0.0',
  category: 'genogram',
  label: 'Parent-Child',
  iconId: 'child',
  propsSchema: childProps,
  defaultProps: () => ({ color: '#000000', width: 2, dashed: false, dotted: false, dash: undefined, endArrow: { type: 'none', size: 0 }, startArrow: { type: 'none', size: 0 }, relation: 'child' }),
  svg: ({ points, props }) => {
    const childProps = props as ChildSvgProps;
    const color = childProps.color || '#334155';
    const width = coerceNumber(childProps.width) ?? 2;
    const dash = childProps.dashed ? (childProps.dash?.join(',') || '6,6') : undefined;
    const routingType = (childProps as any).routingType || 'straight';
    const d = polylineD(points, routingType);
    const markers: string[] = [];
    let markerEnd = '';
    if (childProps.endArrow?.type && childProps.endArrow.type !== 'none') {
      const id = `arrow-end-${Math.random().toString(36).slice(2)}`;
      markers.push(svgArrowMarker(id, color, childProps.endArrow.size || 8, childProps.endArrow.type));
      markerEnd = ` marker-end="url(#${id})"`;
    }
    const extension = childProps.__exportChildExtension;
    const extensionSvg = extension
      ? `<path d="M ${extension.x1} ${extension.y} L ${extension.x2} ${extension.y}" fill="none" stroke="${color}" stroke-width="${width}"${dash ? ` stroke-dasharray="${dash}"` : ''} />`
      : '';
    return `${markers.length ? `<defs>${markers.join('')}</defs>` : ''}${extensionSvg}<path d="${d}" fill="none" stroke="${color}" stroke-width="${width}"${dash ? ` stroke-dasharray="${dash}"` : ''}${markerEnd} />`;
  }
};

// Variants: cohabitation/separation/engagement render like marriage line with style differences
const cohabProps = marriageProps.extend({ relation: z.literal('cohabitation').default('cohabitation') });
export const CohabitationConnectorManifest: ConnectorManifest<z.infer<typeof cohabProps>> = {
  id: 'cohabitation',
  version: '1.0.0',
  category: 'genogram',
  label: 'Cohabitation',
  iconId: 'cohabitation',
  propsSchema: cohabProps,
  defaultProps: () => ({
    color: '#64748B',
    width: 2,
    dashed: true,
    house: true,
    endArrow: { type: 'bar', size: 6 },
    startArrow: { type: 'none', size: 0 },
    relation: 'cohabitation',
  }),
  svg: ({ points, props }) => {
    const color = props.color || '#64748B';
    const width = coerceNumber(props.width) ?? 2;
    const dash = '6,6';
    const routingType = (props as any).routingType || 'straight';
    const d = polylineD(points, routingType);
    const horizontal = findHorizontalSegment(points);
    const rawHouse = (props as Record<string, unknown>).house;
    const showHouse = rawHouse !== false;
    const houseSvg =
      horizontal && showHouse ? houseMarkerOnHorizontalSegment(horizontal, String(color), width) : '';
    return `<path d="${d}" fill="none" stroke="${color}" stroke-width="${width}" stroke-dasharray="${dash}" />${houseSvg}`;
  },
  konvaNative: marriageKonvaNative,
};

const legalCohabProps = marriageProps.extend({ relation: z.literal('legal_cohabitation').default('legal_cohabitation') });
export const LegalCohabitationConnectorManifest: ConnectorManifest<z.infer<typeof legalCohabProps>> = {
  id: 'legal_cohabitation',
  version: '1.0.0',
  category: 'genogram',
  label: 'Legal Cohabitation',
  iconId: 'legal_cohabitation',
  propsSchema: legalCohabProps,
  defaultProps: () => ({
    color: '#2563eb',
    width: 2,
    dashed: true,
    dashDot: true,
    dash: [12, 4, 2, 4],
    endArrow: { type: 'none', size: 0 },
    startArrow: { type: 'none', size: 0 },
    relation: 'legal_cohabitation',
    house: true
  }),
  svg: ({ points, props }) => {
    const color = props.color || '#2563eb';
    const width = props.width || 2;
    const dash = '12,4,2,4';
    const routingType = (props as any).routingType || 'straight';
    const d = polylineD(points, routingType);
    const horizontal = findHorizontalSegment(points);
    const houseSvg = horizontal ? houseMarkerOnHorizontalSegment(horizontal, color, width) : '';
    return `<path d="${d}" fill="none" stroke="${color}" stroke-width="${width}" stroke-dasharray="${dash}" />${houseSvg}`;
  },
  konvaNative: marriageKonvaNative,
};

const legalCohabSeparationProps = marriageProps.extend({ relation: z.literal('legal_cohabitation_separation').default('legal_cohabitation_separation') });
export const LegalCohabitationSeparationConnectorManifest: ConnectorManifest<z.infer<typeof legalCohabSeparationProps>> = {
  id: 'legal_cohabitation_separation',
  version: '1.0.0',
  category: 'genogram',
  label: 'Legal Cohabitation and Separation in Fact',
  iconId: 'legal_cohabitation_separation',
  propsSchema: legalCohabSeparationProps,
  defaultProps: () => ({
    color: '#2563eb',
    width: 2,
    dashed: true,
    dashDot: true,
    dash: [12, 4, 2, 4],
    endArrow: { type: 'none', size: 0 },
    startArrow: { type: 'none', size: 0 },
    relation: 'legal_cohabitation_separation',
    separatedInFact: true
  }),
  svg: ({ points, props }) => {
    const color = props.color || '#2563eb';
    const width = props.width || 2;
    const dash = '12,4,2,4';
    const routingType = (props as any).routingType || 'straight';
    const d = polylineD(points, routingType);
    const horizontal = findHorizontalSegment(points);
    let slashSvg = '';
    if (horizontal) {
      const midX = (horizontal.x1 + horizontal.x2) / 2;
      const midY = horizontal.y;
      slashSvg = `<path d="M ${midX - 6} ${midY - 6} L ${midX + 6} ${midY + 6}" stroke="${color}" stroke-width="${width}" />`;
    }
    return `<path d="${d}" fill="none" stroke="${color}" stroke-width="${width}" stroke-dasharray="${dash}" />${slashSvg}`;
  },
  konvaNative: marriageKonvaNative,
};

const cohabSeparationProps = marriageProps.extend({ relation: z.literal('cohabitation_separation').default('cohabitation_separation') });
export const CohabitationSeparationConnectorManifest: ConnectorManifest<z.infer<typeof cohabSeparationProps>> = {
  id: 'cohabitation_separation',
  version: '1.0.0',
  category: 'genogram',
  label: 'Cohabitation and Separation',
  iconId: 'cohabitation_separation',
  propsSchema: cohabSeparationProps,
  defaultProps: () => ({
    color: '#2563eb',
    width: 2,
    dashed: true,
    dashDoubleDot: true,
    dash: [12, 4, 2, 4, 2, 4],
    endArrow: { type: 'none', size: 0 },
    startArrow: { type: 'none', size: 0 },
    relation: 'cohabitation_separation',
    cohabSeparation: true
  }),
  svg: ({ points, props }) => {
    const color = props.color || '#2563eb';
    const width = props.width || 2;
    const dash = '12,4,2,4,2,4';
    const routingType = (props as any).routingType || 'straight';
    const d = polylineD(points, routingType);
    const horizontal = findHorizontalSegment(points);
    let slashSvg = '';
    if (horizontal) {
      const midX = (horizontal.x1 + horizontal.x2) / 2;
      const midY = horizontal.y;
      slashSvg = `<path d="M ${midX - 6} ${midY - 6} L ${midX + 6} ${midY + 6}" stroke="${color}" stroke-width="${width}" />`;
    }
    return `<path d="${d}" fill="none" stroke="${color}" stroke-width="${width}" stroke-dasharray="${dash}" />${slashSvg}`;
  },
  konvaNative: marriageKonvaNative,
};

const nonSentimentalCohabSepProps = marriageProps.extend({ relation: z.literal('non_sentimental_cohab_separation').default('non_sentimental_cohab_separation') });
export const NonSentimentalCohabSeparationConnectorManifest: ConnectorManifest<z.infer<typeof nonSentimentalCohabSepProps>> = {
  id: 'non_sentimental_cohab_separation',
  version: '1.0.0',
  category: 'genogram',
  label: 'Non-sentimental Cohabitation and Separation',
  iconId: 'non_sentimental_cohab_separation',
  propsSchema: nonSentimentalCohabSepProps,
  defaultProps: () => ({
    color: '#2563eb',
    width: 2,
    dashed: true,
    dashDoubleDot: true,
    dash: [12, 8, 2, 8, 2, 8],
    endArrow: { type: 'none', size: 0 },
    startArrow: { type: 'none', size: 0 },
    relation: 'non_sentimental_cohab_separation',
    nonSentimentalCohabSep: true
  }),
  svg: ({ points, props }) => {
    const color = props.color || '#2563eb';
    const width = props.width || 2;
    const dash = '12,8,2,8,2,8';
    const routingType = (props as any).routingType || 'straight';
    const d = polylineD(points, routingType);
    const horizontal = findHorizontalSegment(points);
    let slashSvg = '';
    if (horizontal) {
      const midX = (horizontal.x1 + horizontal.x2) / 2;
      const midY = horizontal.y;
      slashSvg = `<path d="M ${midX - 6} ${midY - 6} L ${midX + 6} ${midY + 6}" stroke="${color}" stroke-width="${width}" />`;
    }
    return `<path d="${d}" fill="none" stroke="${color}" stroke-width="${width}" stroke-dasharray="${dash}" />${slashSvg}`;
  },
  konvaNative: marriageKonvaNative,
};

const nonSentimentalCohabProps = marriageProps.extend({ relation: z.literal('non_sentimental_cohabitation').default('non_sentimental_cohabitation') });
export const NonSentimentalCohabitationConnectorManifest: ConnectorManifest<z.infer<typeof nonSentimentalCohabProps>> = {
  id: 'non_sentimental_cohabitation',
  version: '1.0.0',
  category: 'genogram',
  label: 'Non-sentimental Cohabitation',
  iconId: 'non_sentimental_cohabitation',
  propsSchema: nonSentimentalCohabProps,
  defaultProps: () => ({
    color: '#2563eb',
    width: 2,
    dashed: true,
    dashDoubleDot: true,
    dash: [12, 8, 2, 8, 2, 8],
    house: true,
    endArrow: { type: 'none', size: 0 },
    startArrow: { type: 'none', size: 0 },
    relation: 'non_sentimental_cohabitation',
    nonSentimentalCohab: true
  }),
  svg: ({ points, props }) => {
    const color = props.color || '#2563eb';
    const width = props.width || 2;
    const dash = '12,8,2,8,2,8';
    const routingType = (props as any).routingType || 'straight';
    const d = polylineD(points, routingType);
    const horizontal = findHorizontalSegment(points);
    let houseSvg = '';
    if (horizontal) {
      const midX = (horizontal.x1 + horizontal.x2) / 2;
      const midY = horizontal.y;
      const houseWidth = 10;
      const houseHeight = 8;
      const roofHeight = 6;
      houseSvg = `<path d="M ${midX - houseWidth / 2} ${midY + houseHeight / 2} L ${midX - houseWidth / 2} ${midY - houseHeight / 2 + roofHeight} L ${midX} ${midY - houseHeight / 2} L ${midX + houseWidth / 2} ${midY - houseHeight / 2 + roofHeight} L ${midX + houseWidth / 2} ${midY + houseHeight / 2} Z" fill="white" stroke="${color}" stroke-width="${width}" stroke-linejoin="round" />`;
    }
    return `<path d="${d}" fill="none" stroke="${color}" stroke-width="${width}" stroke-dasharray="${dash}" />${houseSvg}`;
  },
  konvaNative: marriageKonvaNative,
};

const temporaryRelationProps = marriageProps.extend({ relation: z.literal('temporary_relation').default('temporary_relation') });
export const TemporaryRelationConnectorManifest: ConnectorManifest<z.infer<typeof temporaryRelationProps>> = {
  id: 'temporary_relation',
  version: '1.0.0',
  category: 'genogram',
  label: 'Temporary relation / One night stand',
  iconId: 'temporary_relation',
  propsSchema: temporaryRelationProps,
  defaultProps: () => ({
    color: '#2563eb',
    width: 2,
    dashed: true,
    dashSingleDot: true,
    dash: [12, 8, 2, 8],
    endArrow: { type: 'none', size: 0 },
    startArrow: { type: 'none', size: 0 },
    relation: 'temporary_relation',
    temporaryRelation: true
  }),
  svg: ({ points, props }) => {
    const color = props.color || '#2563eb';
    const width = props.width || 2;
    const dash = '12,8,2,8';
    const routingType = (props as any).routingType || 'straight';
    const d = polylineD(points, routingType);
    return `<path d="${d}" fill="none" stroke="${color}" stroke-width="${width}" stroke-dasharray="${dash}" />`;
  },
  konvaNative: marriageKonvaNative,
};

const legalCohabLegalSepProps = marriageProps.extend({ relation: z.literal('legal_cohab_legal_separation').default('legal_cohab_legal_separation') });
export const LegalCohabLegalSeparationConnectorManifest: ConnectorManifest<z.infer<typeof legalCohabLegalSepProps>> = {
  id: 'legal_cohab_legal_separation',
  version: '1.0.0',
  category: 'genogram',
  label: 'Legal cohabitation and official (legal) separation',
  iconId: 'legal_cohab_legal_separation',
  propsSchema: legalCohabLegalSepProps,
  defaultProps: () => ({
    color: '#2563eb',
    width: 2,
    dashed: true,
    dashSingleDot: true,
    dash: [12, 4, 2, 4],
    endArrow: { type: 'none', size: 0 },
    startArrow: { type: 'none', size: 0 },
    relation: 'legal_cohab_legal_separation',
    legalCohabLegalSep: true
  }),
  svg: ({ points, props }) => {
    const color = props.color || '#2563eb';
    const width = props.width || 2;
    const dash = '12,4,2,4';
    const routingType = (props as any).routingType || 'straight';
    const d = polylineD(points, routingType);
    const horizontal = findHorizontalSegment(points);
    let slashSvg = '';
    if (horizontal) {
      const midX = (horizontal.x1 + horizontal.x2) / 2;
      const midY = horizontal.y;
      // Backward slash: from top-right to bottom-left
      slashSvg = `<path d="M ${midX + 6} ${midY - 6} L ${midX - 6} ${midY + 6}" stroke="${color}" stroke-width="${width}" />`;
    }
    return `<path d="${d}" fill="none" stroke="${color}" stroke-width="${width}" stroke-dasharray="${dash}" />${slashSvg}`;
  },
  konvaNative: marriageKonvaNative,
};

const casualRelSepProps = marriageProps.extend({ relation: z.literal('casual_relationship_separation').default('casual_relationship_separation') });
export const CasualRelationshipSeparationConnectorManifest: ConnectorManifest<z.infer<typeof casualRelSepProps>> = {
  id: 'casual_relationship_separation',
  version: '1.0.0',
  category: 'genogram',
  label: 'Casual Relationship and Separation',
  iconId: 'casual_relationship_separation',
  propsSchema: casualRelSepProps,
  defaultProps: () => ({
    color: '#2563eb',
    width: 2,
    dashed: true,
    dotted: true,
    dash: [2, 4],
    endArrow: { type: 'none', size: 0 },
    startArrow: { type: 'none', size: 0 },
    relation: 'casual_relationship_separation',
    casualRelSep: true
  }),
  svg: ({ points, props }) => {
    const color = props.color || '#2563eb';
    const width = props.width || 2;
    const dash = '2,4';
    const routingType = (props as any).routingType || 'straight';
    const d = polylineD(points, routingType);
    const horizontal = findHorizontalSegment(points);
    let slashSvg = '';
    if (horizontal) {
      const midX = (horizontal.x1 + horizontal.x2) / 2;
      const midY = horizontal.y;
      // Forward slash: from top-left to bottom-right
      slashSvg = `<path d="M ${midX - 6} ${midY - 6} L ${midX + 6} ${midY + 6}" stroke="${color}" stroke-width="${width}" />`;
    }
    return `<path d="${d}" fill="none" stroke="${color}" stroke-width="${width}" stroke-dasharray="${dash}" />${slashSvg}`;
  },
  konvaNative: marriageKonvaNative,
};

// Separation in Fact - straight line with backward slash (\) in center, red
const separationInFactProps = marriageProps.extend({ relation: z.literal('separation_in_fact').default('separation_in_fact') });
export const SeparationInFactConnectorManifest: ConnectorManifest<z.infer<typeof separationInFactProps>> = {
  id: 'separation_in_fact',
  version: '1.0.0',
  category: 'genogram',
  label: 'Separation in fact',
  iconId: 'separation_in_fact',
  propsSchema: separationInFactProps,
  defaultProps: () => ({
    color: '#DC0101',
    width: 2,
    dashed: false,
    endArrow: { type: 'none', size: 0 },
    startArrow: { type: 'none', size: 0 },
    relation: 'separation_in_fact',
    separationInFactSlash: true
  }),
  svg: ({ points, props }) => {
    const color = props.color || '#DC0101';
    const width = props.width || 2;
    const routingType = (props as any).routingType || 'straight';
    const d = polylineD(points, routingType);
    const horizontal = findHorizontalSegment(points);
    let slashSvg = '';
    if (horizontal) {
      const midX = (horizontal.x1 + horizontal.x2) / 2;
      const midY = horizontal.y;
      // Forward slash: from top-left to bottom-right
      slashSvg = `<path d="M ${midX - 6} ${midY - 6} L ${midX + 6} ${midY + 6}" stroke="${color}" stroke-width="${width}" />`;
    }
    return `<path d="${d}" fill="none" stroke="${color}" stroke-width="${width}" />${slashSvg}`;
  },
  konvaNative: marriageKonvaNative,
};

// Legal Separation - straight line with backward slash (\) in center, red
const legalSeparationProps = marriageProps.extend({ relation: z.literal('legal_separation').default('legal_separation') });
export const LegalSeparationConnectorManifest: ConnectorManifest<z.infer<typeof legalSeparationProps>> = {
  id: 'legal_separation',
  version: '1.0.0',
  category: 'genogram',
  label: 'Legal Separation',
  iconId: 'legal_separation',
  propsSchema: legalSeparationProps,
  defaultProps: () => ({
    color: '#DC0101',
    width: 2,
    dashed: false,
    endArrow: { type: 'none', size: 0 },
    startArrow: { type: 'none', size: 0 },
    relation: 'legal_separation',
    legalSeparationSlash: true
  }),
  svg: ({ points, props }) => {
    const color = props.color || '#DC0101';
    const width = props.width || 2;
    const routingType = (props as any).routingType || 'straight';
    const d = polylineD(points, routingType);
    const horizontal = findHorizontalSegment(points);
    let slashSvg = '';
    if (horizontal) {
      const midX = (horizontal.x1 + horizontal.x2) / 2;
      const midY = horizontal.y;
      // Backward slash: from top-right to bottom-left
      slashSvg = `<path d="M ${midX + 6} ${midY - 6} L ${midX - 6} ${midY + 6}" stroke="${color}" stroke-width="${width}" />`;
    }
    return `<path d="${d}" fill="none" stroke="${color}" stroke-width="${width}" />${slashSvg}`;
  },
  konvaNative: marriageKonvaNative,
};

// Nullity - straight line with 3 forward slashes in center, red
const nullityProps = marriageProps.extend({ relation: z.literal('nullity').default('nullity') });
export const NullityConnectorManifest: ConnectorManifest<z.infer<typeof nullityProps>> = {
  id: 'nullity',
  version: '1.0.0',
  category: 'genogram',
  label: 'Nullity',
  iconId: 'nullity',
  propsSchema: nullityProps,
  defaultProps: () => ({
    color: '#DC0101',
    width: 2,
    dashed: false,
    endArrow: { type: 'none', size: 0 },
    startArrow: { type: 'none', size: 0 },
    relation: 'nullity',
    nullity: true
  }),
  svg: ({ points, props }) => {
    const color = props.color || '#DC0101';
    const width = props.width || 2;
    const routingType = (props as any).routingType || 'straight';
    const d = polylineD(points, routingType);
    const horizontal = findHorizontalSegment(points);
    let slashesSvg = '';
    if (horizontal) {
      const midX = (horizontal.x1 + horizontal.x2) / 2;
      const midY = horizontal.y;
      // 3 backward slashes
      slashesSvg = `
        <path d="M ${midX - 6} ${midY - 6} L ${midX - 12} ${midY + 6}" stroke="${color}" stroke-width="${width}" />
        <path d="M ${midX + 3} ${midY - 6} L ${midX - 3} ${midY + 6}" stroke="${color}" stroke-width="${width}" />
        <path d="M ${midX + 12} ${midY - 6} L ${midX + 6} ${midY + 6}" stroke="${color}" stroke-width="${width}" />
      `;
    }
    return `<path d="${d}" fill="none" stroke="${color}" stroke-width="${width}" />${slashesSvg}`;
  },
  konvaNative: marriageKonvaNative,
};

// Harmony - straight line, green
const harmonyProps = connectorCommonProps.extend({ relation: z.literal('harmony').default('harmony') });
export const HarmonyConnectorManifest: ConnectorManifest<z.infer<typeof harmonyProps>> = {
  id: 'harmony',
  version: '1.0.0',
  category: 'genogram',
  label: 'Harmony',
  iconId: 'harmony',
  propsSchema: harmonyProps,
  defaultProps: () => ({
    color: '#01A001',
    width: 3,
    dashed: false,
    endArrow: { type: 'none', size: 0 },
    startArrow: { type: 'none', size: 0 },
    relation: 'harmony',
    routingType: 'bezier'
  }),
  svg: ({ points, props }) => {
    const color = props.color || '#01A001';
    const width = props.width || 3;
    const routingType = (props as any).routingType || 'bezier';
    const d = polylineD(points, routingType);
    return `<path d="${d}" fill="none" stroke="${color}" stroke-width="${width}" />`;
  }
};

// In Love (Harmony) - straight line with double overlapping circles, green
const inLoveHarmonyProps = connectorCommonProps.extend({ relation: z.literal('in_love_harmony').default('in_love_harmony') });
export const InLoveHarmonyConnectorManifest: ConnectorManifest<z.infer<typeof inLoveHarmonyProps>> = {
  id: 'in_love_harmony',
  version: '1.0.0',
  category: 'genogram',
  label: 'In Love',
  iconId: 'in_love_harmony',
  propsSchema: inLoveHarmonyProps,
  defaultProps: () => ({
    color: '#01A001',
    width: 3,
    dashed: false,
    endArrow: { type: 'none', size: 0 },
    startArrow: { type: 'none', size: 0 },
    relation: 'in_love_harmony',
    inLoveHarmony: true,
    routingType: 'bezier'
  }),
  svg: ({ points, props }) => {
    const color = props.color || '#01A001';
    const width = props.width || 3;
    const routingType = (props as any).routingType || 'bezier';
    const d = polylineD(points, routingType);
    const background =
      typeof (props as any).__exportBackground === 'string' && (props as any).__exportBackground.trim().length > 0
        ? (props as any).__exportBackground
        : '#ffffff';
    return `<path d="${d}" fill="none" stroke="${color}" stroke-width="${width}" />${renderInLoveMarker(points, color, width, background)}`;
  }
};

// Emotional Connection / Spiritual Relationship - chain of touching circles (no overlap), green
const emotionalConnectionProps = connectorCommonProps.extend({ relation: z.literal('emotional_connection').default('emotional_connection') });
export const EmotionalConnectionConnectorManifest: ConnectorManifest<z.infer<typeof emotionalConnectionProps>> = {
  id: 'emotional_connection',
  version: '1.0.0',
  category: 'genogram',
  label: 'Emotional Connection / Spiritual Relationship',
  iconId: 'emotional_connection',
  propsSchema: emotionalConnectionProps,
  defaultProps: () => ({
    color: '#01A001',
    width: 2,
    dashed: false,
    endArrow: { type: 'none', size: 0 },
    startArrow: { type: 'none', size: 0 },
    relation: 'emotional_connection',
    emotionalConnection: true,
    routingType: 'bezier'
  }),
  svg: ({ points, props }) => {
    const color = props.color || '#01A001';
    const width = props.width || 2;
    const x1 = points[0];
    const x2 = points[points.length - 2];
    const y1 = points[1];
    const y2 = points[points.length - 1];
    const lineLength = Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
    const circleRadius = 5;
    const spacing = circleRadius * 2; // Circles touch but don't overlap
    const numCircles = Math.max(2, Math.floor(lineLength / spacing));
    let circles = '';
    for (let i = 0; i < numCircles; i++) {
      const t = (i + 0.5) / numCircles;
      const cx = x1 + t * (x2 - x1);
      const cy = y1 + t * (y2 - y1);
      circles += `<circle cx="${cx}" cy="${cy}" r="${circleRadius}" fill="transparent" stroke="${color}" stroke-width="${width}" />`;
    }
    return circles;
  }
};

// Distrust - single horizontal black line with red vertical strokes
const distrustProps = connectorCommonProps.extend({ relation: z.literal('distrust').default('distrust') });
export const DistrustConnectorManifest: ConnectorManifest<z.infer<typeof distrustProps>> = {
  id: 'distrust',
  version: '1.0.0',
  category: 'genogram',
  label: 'Distrust',
  iconId: 'distrust',
  propsSchema: distrustProps,
  defaultProps: () => ({
    color: '#000000',
    width: 3,
    dashed: false,
    endArrow: { type: 'none', size: 0 },
    startArrow: { type: 'none', size: 0 },
    relation: 'distrust',
    distrust: true,
    routingType: 'bezier'
  }),
  svg: ({ points, props }) => {
    const width = props.width || 3;
    const x1 = points[0];
    const x2 = points[points.length - 2];
    const y1 = points[1];
    const y2 = points[points.length - 1];
    const routingType = (props as any).routingType || 'bezier';
    const d = polylineD(points, routingType);
    const numTicks = 15;
    const verticalExtend = 7; // ~14px total height
    let ticks = '';
    for (let i = 0; i < numTicks; i++) {
      const t = (i + 1) / (numTicks + 1);
      const cx = x1 + t * (x2 - x1);
      const cy = y1 + t * (y2 - y1);
      ticks += `<line x1="${cx}" y1="${cy - verticalExtend}" x2="${cx}" y2="${cy + verticalExtend}" stroke="#CA3A31" stroke-width="4" />`;
    }
    return `<path d="${d}" fill="none" stroke="#000000" stroke-width="${width}" />${ticks}`;
  }
};

// Fused - Hostile: 3 black straight lines above + red zigzag line below
const fusedHostileProps = connectorCommonProps.extend({ relation: z.literal('fused_hostile_combined').default('fused_hostile_combined') });
export const FusedHostileConnectorManifest: ConnectorManifest<z.infer<typeof fusedHostileProps>> = {
  id: 'fused_hostile_combined',
  version: '1.0.0',
  category: 'genogram',
  label: 'Fused - Hostile',
  iconId: 'fused_hostile_combined',
  propsSchema: fusedHostileProps,
  defaultProps: () => ({
    color: '#000000',
    width: 3,
    dashed: false,
    endArrow: { type: 'none', size: 0 },
    startArrow: { type: 'none', size: 0 },
    relation: 'fused_hostile_combined',
    fusedHostile: true,
    routingType: 'bezier'
  }),
  svg: ({ points, props }) => {
    const width = props.width || 3;
    const routingType = (props as any).routingType || 'bezier';
    const gap = Math.max(4, width + 1);
    const lines = [-gap, 0, gap]
      .map((offset) => `<path d="${offsetPathD(points, offset, routingType)}" fill="none" stroke="#000000" stroke-width="${width}" />`)
      .join('');
    return `${lines}${renderZigzagPath(points, '#DC0101', width, 5)}`;
  }
};

// Distance - Hostile: 1 black dashed line + red zigzag line overlapping
const distanceHostileProps = connectorCommonProps.extend({ relation: z.literal('distance_hostile').default('distance_hostile') });
export const DistanceHostileConnectorManifest: ConnectorManifest<z.infer<typeof distanceHostileProps>> = {
  id: 'distance_hostile',
  version: '1.0.0',
  category: 'genogram',
  label: 'Distance - Hostile',
  iconId: 'distance_hostile',
  propsSchema: distanceHostileProps,
  defaultProps: () => ({
    color: '#000000',
    width: 3,
    dashed: false,
    endArrow: { type: 'none', size: 0 },
    startArrow: { type: 'none', size: 0 },
    relation: 'distance_hostile',
    distanceHostile: true,
    routingType: 'bezier'
  }),
  svg: ({ points, props }) => {
    const width = props.width || 3;
    const x1 = points[0];
    const x2 = points[points.length - 2];
    const y1 = points[1];
    const y2 = points[points.length - 1];
    const routingType = (props as any).routingType || 'bezier';
    const d = polylineD(points, routingType);
    // Black dashed line
    let result = `<path d="${d}" fill="none" stroke="#000000" stroke-width="${width}" stroke-dasharray="6,6" />`;
    // Red zigzag overlapping through the line
    const waveAmp = 6;
    let zigzagPath = `M ${x1} ${y1}`;
    const steps = 20;
    for (let i = 1; i <= steps; i++) {
      const t = i / steps;
      const cx = x1 + t * (x2 - x1);
      // Alternating up/down for sharp zigzag
      const offset = (i % 2 === 1) ? waveAmp : -waveAmp;
      const cy = y1 + t * (y2 - y1) + offset;
      zigzagPath += ` L ${cx} ${cy}`;
    }
    result += `<path d="${zigzagPath}" fill="none" stroke="#DC0101" stroke-width="${width}" />`;
    return result;
  }
};

// Close - Hostile: 2 black straight lines + red zigzag line overlapping
const closeHostileProps = connectorCommonProps.extend({ relation: z.literal('close_hostile').default('close_hostile') });
export const CloseHostileConnectorManifest: ConnectorManifest<z.infer<typeof closeHostileProps>> = {
  id: 'close_hostile',
  version: '1.0.0',
  category: 'genogram',
  label: 'Close - Hostile',
  iconId: 'close_hostile',
  propsSchema: closeHostileProps,
  defaultProps: () => ({
    color: '#000000',
    width: 3,
    dashed: false,
    endArrow: { type: 'none', size: 0 },
    startArrow: { type: 'none', size: 0 },
    relation: 'close_hostile',
    closeHostile: true,
    routingType: 'bezier'
  }),
  svg: ({ points, props }) => {
    const width = props.width || 3;
    const x1 = points[0];
    const x2 = points[points.length - 2];
    const y1 = points[1];
    const y2 = points[points.length - 1];
    // 2 black lines
    const doubleOffsets = [-3, 3];
    let result = '';
    doubleOffsets.forEach((off) => {
      result += `<line x1="${x1}" y1="${y1 + off}" x2="${x2}" y2="${y2 + off}" stroke="#000000" stroke-width="${width}" />`;
    });
    // Red zigzag overlapping through the lines
    const waveAmp = 6;
    let zigzagPath = `M ${x1} ${y1}`;
    const steps = 20;
    for (let i = 1; i <= steps; i++) {
      const t = i / steps;
      const cx = x1 + t * (x2 - x1);
      // Alternating up/down for sharp zigzag
      const offset = (i % 2 === 1) ? waveAmp : -waveAmp;
      const cy = y1 + t * (y2 - y1) + offset;
      zigzagPath += ` L ${cx} ${cy}`;
    }
    result += `<path d="${zigzagPath}" fill="none" stroke="#DC0101" stroke-width="${width}" />`;
    return result;
  }
};

// Never Met - straight line with square with X inside, gray
const neverMetProps = connectorCommonProps.extend({ relation: z.literal('never_met').default('never_met') });
export const NeverMetConnectorManifest: ConnectorManifest<z.infer<typeof neverMetProps>> = {
  id: 'never_met',
  version: '1.0.0',
  category: 'genogram',
  label: 'Never Met',
  iconId: 'never_met',
  propsSchema: neverMetProps,
  defaultProps: () => ({
    color: '#6b7280',
    width: 2,
    dashed: false,
    endArrow: { type: 'none', size: 0 },
    startArrow: { type: 'none', size: 0 },
    relation: 'never_met',
    neverMet: true,
    routingType: 'bezier'
  }),
  svg: ({ points, props }) => {
    const color = props.color || '#6b7280';
    const width = props.width || 2;
    const routingType = (props as any).routingType || 'bezier';
    const d = polylineD(points, routingType);
    const midX = (points[0] + points[points.length - 2]) / 2;
    const midY = (points[1] + points[points.length - 1]) / 2;
    const size = 8;
    // Square with X inside - X goes corner to corner
    const square = `<rect x="${midX - size}" y="${midY - size}" width="${size * 2}" height="${size * 2}" fill="white" stroke="${color}" stroke-width="${width}" />`;
    const xMark = `<path d="M ${midX - size} ${midY - size} L ${midX + size} ${midY + size} M ${midX + size} ${midY - size} L ${midX - size} ${midY + size}" stroke="${color}" stroke-width="${width}" />`;
    return `<path d="${d}" fill="none" stroke="${color}" stroke-width="${width}" />${square}${xMark}`;
  }
};

// Cutoff Repaired - dashed line with vertical stroke and small circle, green
const cutoffRepairedProps = connectorCommonProps.extend({ relation: z.literal('cutoff_repaired').default('cutoff_repaired') });
export const CutoffRepairedConnectorManifest: ConnectorManifest<z.infer<typeof cutoffRepairedProps>> = {
  id: 'cutoff_repaired',
  version: '1.0.0',
  category: 'genogram',
  label: 'Cutoff repaired',
  iconId: 'cutoff_repaired',
  propsSchema: cutoffRepairedProps,
  defaultProps: () => ({
    color: '#01A001',
    width: 3,
    dashed: true,
    dash: [6, 6],
    endArrow: { type: 'none', size: 0 },
    startArrow: { type: 'none', size: 0 },
    relation: 'cutoff_repaired',
    cutoffRepaired: true,
    routingType: 'bezier'
  }),
  svg: ({ points, props }) => {
    const color = props.color || '#01A001';
    const width = props.width || 3;
    const dash = '6,6';
    const routingType = (props as any).routingType || 'bezier';
    const d = polylineD(points, routingType);
    const midX = (points[0] + points[points.length - 2]) / 2;
    const midY = (points[1] + points[points.length - 1]) / 2;
    // Two vertical strokes (ticks) on each side
    const leftTick = `<path d="M ${midX - 6} ${midY - 6} L ${midX - 6} ${midY + 6}" stroke="${color}" stroke-width="${width}" />`;
    const rightTick = `<path d="M ${midX + 6} ${midY - 6} L ${midX + 6} ${midY + 6}" stroke="${color}" stroke-width="${width}" />`;
    // Small circle in the center between the ticks
    const circle = `<circle cx="${midX}" cy="${midY}" r="4" fill="none" stroke="${color}" stroke-width="${width}" />`;
    return `<path d="${d}" fill="none" stroke="${color}" stroke-width="${width}" stroke-dasharray="${dash}" />${leftTick}${circle}${rightTick}`;
  }
};

// Love Affair - dash-dot line with increased gaps, shadow red (blur)
const loveAffairProps = connectorCommonProps.extend({ relation: z.literal('love_affair').default('love_affair') });
export const LoveAffairConnectorManifest: ConnectorManifest<z.infer<typeof loveAffairProps>> = {
  id: 'love_affair',
  version: '1.0.0',
  category: 'genogram',
  label: 'Love affair',
  iconId: 'love_affair',
  propsSchema: loveAffairProps,
  defaultProps: () => ({
    color: '#DC010180',
    width: 3,
    dashed: true,
    dashDot: true,
    dash: [12, 8, 2, 8],
    endArrow: { type: 'none', size: 0 },
    startArrow: { type: 'none', size: 0 },
    relation: 'love_affair',
    loveAffair: true,
    routingType: 'bezier'
  }),
  svg: ({ points, props }) => {
    const color = props.color || '#DC010180';
    const width = props.width || 3;
    const dash = '12,8,2,8';
    const routingType = (props as any).routingType || 'bezier';
    const d = polylineD(points, routingType);
    return `<path d="${d}" fill="none" stroke="${color}" stroke-width="${width}" stroke-dasharray="${dash}" />`;
  }
};

const separationProps = marriageProps.extend({ relation: z.literal('separation').default('separation') });
export const SeparationConnectorManifest: ConnectorManifest<z.infer<typeof separationProps>> = {
  id: 'separation',
  version: '1.0.0',
  category: 'genogram',
  label: 'Separation/Divorce',
  iconId: 'separation',
  propsSchema: separationProps,
  defaultProps: () => ({ color: '#000000', width: 2, dashed: true, endArrow: { type: 'bar', size: 6 }, startArrow: { type: 'none', size: 0 }, relation: 'separation' }),
  konvaNative: marriageKonvaNative,
  svg: ({ points, props }) => {
    const color = props.color || '#64748B';
    const width = props.width || 2;
    const dash = '3,4';
    const routingType = (props as any).routingType || 'straight';
    const d = polylineD(points, routingType);
    // Add short cross tick at the middle
    const midIndex = Math.floor(points.length / 2);
    const mx = points[midIndex - 2] || 0;
    const my = points[midIndex - 1] || 0;
    return `<g><path d="${d}" fill="none" stroke="${color}" stroke-width="${width}" stroke-dasharray="${dash}" />`+
      `<path d="M ${mx - 6} ${my - 6} L ${mx + 6} ${my + 6}" stroke="${color}" stroke-width="${width}" /></g>`;
  }
};

const engagementProps = marriageProps.extend({ relation: z.literal('engagement').default('engagement') });
export const EngagementConnectorManifest: ConnectorManifest<z.infer<typeof engagementProps>> = {
  id: 'engagement',
  version: '1.0.0',
  category: 'genogram',
  label: 'Engagement',
  iconId: 'engagement',
  propsSchema: engagementProps,
  defaultProps: () => ({ color: '#0EA5E9', width: 2, dashed: false, endArrow: { type: 'circle', size: 6 }, startArrow: { type: 'none', size: 0 }, relation: 'engagement' }),
  svg: ({ points, props }) => {
    const color = props.color || '#0EA5E9';
    const width = props.width || 2;
    const routingType = (props as any).routingType || 'straight';
    const d = polylineD(points, routingType);
    return `<path d="${d}" fill="none" stroke="${color}" stroke-width="${width}" stroke-dasharray="2,4" />`;
  }
};

const committedRelProps = marriageProps.extend({ relation: z.literal('committed_relationship').default('committed_relationship') });
export const CommittedRelationshipConnectorManifest: ConnectorManifest<z.infer<typeof committedRelProps>> = {
  id: 'committed_relationship',
  version: '1.0.0',
  category: 'genogram',
  label: 'Committed Relationship',
  iconId: 'committed_relationship',
  propsSchema: committedRelProps,
  defaultProps: () => ({
    color: '#2563eb',
    width: 2,
    dashed: false,
    solidDashedDouble: true,
    dash: [6, 6],
    endArrow: { type: 'none', size: 0 },
    startArrow: { type: 'none', size: 0 },
    relation: 'committed_relationship'
  }),
  svg: ({ points, props }) => {
    const color = props.color || '#2563eb';
    const width = props.width || 2;
    const routingType = (props as any).routingType || 'straight';
    const d = polylineD(points, routingType);
    // Offset for double line effect
    const offset = 3.5;
    // Create two parallel paths - one solid, one dashed
    return `<g>
      <path d="${d}" fill="none" stroke="${color}" stroke-width="${width}" transform="translate(0, ${-offset})" />
      <path d="${d}" fill="none" stroke="${color}" stroke-width="${width}" stroke-dasharray="6,6" transform="translate(0, ${offset})" />
    </g>`;
  },
  konvaNative: marriageKonvaNative,
};

// Sibling bar + twins
const siblingProps = connectorCommonProps.extend({ relation: z.literal('sibling').default('sibling') });
export const SiblingConnectorManifest: ConnectorManifest<z.infer<typeof siblingProps>> = {
  id: 'sibling',
  version: '1.0.0',
  category: 'genogram',
  label: 'Sibling',
  iconId: 'sibling',
  propsSchema: siblingProps,
  defaultProps: () => ({ color: '#111827', width: 2, dashed: false, startArrow: { type: 'none', size: 0 }, endArrow: { type: 'triangle', size: 8 }, relation: 'sibling' }),
  svg: ({ points, props }) => {
    const color = props.color || '#334155';
    const width = props.width || 2;
    const routingType = (props as any).routingType || 'straight';
    const dash = resolveLineStrokeDash(props as Record<string, unknown>);
    const arrows = resolveLineArrowExport(props as Record<string, unknown>, points, color);
    const d = polylineD(arrows.points, routingType);
    return `${arrows.defs}<path d="${d}" fill="none" stroke="${color}" stroke-width="${width}"${dash ? ` stroke-dasharray="${dash}"` : ''}${arrows.attrs} />${arrows.arrowSvg}`;
  }
};

const halfSiblingProps = siblingProps.extend({ relation: z.literal('half_siblings').default('half_siblings') });
export const HalfSiblingConnectorManifest: ConnectorManifest<z.infer<typeof halfSiblingProps>> = {
  id: 'half-siblings',
  version: '1.0.0',
  category: 'genogram',
  label: 'Half-sibling',
  iconId: 'half-sibling',
  propsSchema: halfSiblingProps,
  defaultProps: () => ({ color: '#111827', width: 2, dashed: false, startArrow: { type: 'none', size: 0 }, endArrow: { type: 'triangle', size: 8 }, relation: 'half_siblings' }),
  svg: ({ points, props }) => {
    const color = props.color || '#64748B';
    const width = props.width || 2;
    const routingType = (props as any).routingType || 'straight';
    const dash = resolveLineStrokeDash(props as Record<string, unknown>);
    const arrows = resolveLineArrowExport(props as Record<string, unknown>, points, color);
    const d = polylineD(arrows.points, routingType);
    return `${arrows.defs}<path d="${d}" fill="none" stroke="${color}" stroke-width="${width}"${dash ? ` stroke-dasharray="${dash}"` : ''}${arrows.attrs} />${arrows.arrowSvg}`;
  }
};

const twinMonoProps = siblingProps.extend({ relation: z.literal('twin_mono').default('twin_mono') });
export const TwinMonoConnectorManifest: ConnectorManifest<z.infer<typeof twinMonoProps>> = {
  id: 'twin-mono',
  version: '1.0.0',
  category: 'genogram',
  label: 'Twin (Mono)',
  iconId: 'twin-mono',
  propsSchema: twinMonoProps,
  defaultProps: () => ({ color: '#334155', width: 2, dashed: false, startArrow: { type: 'none', size: 0 }, endArrow: { type: 'triangle', size: 8 }, relation: 'twin_mono' }),
  svg: ({ points, props }) => {
    const color = props.color || '#334155';
    const width = props.width || 2;
    const routingType = (props as any).routingType || 'straight';
    const dash = resolveLineStrokeDash(props as Record<string, unknown>);
    const dashAttr = dash ? ` stroke-dasharray="${dash}"` : '';
    const arrows = resolveLineArrowExport(props as Record<string, unknown>, points, color);
    const d = polylineD(arrows.points, routingType);
    return `<g>${arrows.defs}<path d="${d}" fill="none" stroke="${color}" stroke-width="${width}"${dashAttr}${arrows.attrs} />`+
      `${arrows.arrowSvg}<circle cx="${points[0]}" cy="${points[1]}" r="2" fill="${color}" />`+
      `<circle cx="${points[points.length-2]}" cy="${points[points.length-1]}" r="2" fill="${color}" /></g>`;
  }
};

const twinDiProps = siblingProps.extend({ relation: z.literal('twin_di').default('twin_di') });
export const TwinDiConnectorManifest: ConnectorManifest<z.infer<typeof twinDiProps>> = {
  id: 'twin-di',
  version: '1.0.0',
  category: 'genogram',
  label: 'Twin (Di)',
  iconId: 'twin-di',
  propsSchema: twinDiProps,
  defaultProps: () => ({ color: '#334155', width: 2, dashed: false, startArrow: { type: 'none', size: 0 }, endArrow: { type: 'triangle', size: 8 }, relation: 'twin_di' }),
  svg: ({ points, props }) => {
    const color = props.color || '#334155';
    const width = props.width || 2;
    const routingType = (props as any).routingType || 'straight';
    const dash = resolveLineStrokeDash(props as Record<string, unknown>);
    const arrows = resolveLineArrowExport(props as Record<string, unknown>, points, color);
    const d = polylineD(arrows.points, routingType);
    return `${arrows.defs}<path d="${d}" fill="none" stroke="${color}" stroke-width="${width}"${dash ? ` stroke-dasharray="${dash}"` : ''}${arrows.attrs} />${arrows.arrowSvg}`;
  }
};

export function registerGenogramConnectors() {
  void _childKonvaRenderer;
  void _emotionalKonvaRenderer;

  registerConnector('genogram', LineConnectorManifest);
  registerConnector('genogram', MarriageConnectorManifest);
  registerConnector('genogram', ChildConnectorManifest);
  registerConnector('genogram', CohabitationConnectorManifest);
  registerConnector('genogram', LegalCohabitationConnectorManifest);
  registerConnector('genogram', LegalCohabitationSeparationConnectorManifest);
  registerConnector('genogram', CohabitationSeparationConnectorManifest);
  registerConnector('genogram', NonSentimentalCohabSeparationConnectorManifest);
  registerConnector('genogram', NonSentimentalCohabitationConnectorManifest);
  registerConnector('genogram', TemporaryRelationConnectorManifest);
  registerConnector('genogram', LegalCohabLegalSeparationConnectorManifest);
  registerConnector('genogram', CasualRelationshipSeparationConnectorManifest);
  registerConnector('genogram', SeparationInFactConnectorManifest);
  registerConnector('genogram', LegalSeparationConnectorManifest);
  registerConnector('genogram', NullityConnectorManifest);
  registerConnector('genogram', LoveAffairConnectorManifest);
  registerConnector('genogram', CutoffRepairedConnectorManifest);
  registerConnector('genogram', NeverMetConnectorManifest);
  registerConnector('genogram', HarmonyConnectorManifest);
  registerConnector('genogram', InLoveHarmonyConnectorManifest);
  registerConnector('genogram', EmotionalConnectionConnectorManifest);
  registerConnector('genogram', DistrustConnectorManifest);
  registerConnector('genogram', FusedHostileConnectorManifest);
  registerConnector('genogram', DistanceHostileConnectorManifest);
  registerConnector('genogram', CloseHostileConnectorManifest);
  registerConnector('genogram', SeparationConnectorManifest);
  registerConnector('genogram', EngagementConnectorManifest);
  registerConnector('genogram', CommittedRelationshipConnectorManifest);
  registerConnector('genogram', SiblingConnectorManifest);
  registerConnector('genogram', HalfSiblingConnectorManifest);
  registerConnector('genogram', TwinMonoConnectorManifest);
  registerConnector('genogram', TwinDiConnectorManifest);
}
